import React, { createContext, useContext, useState, useCallback } from 'react';
import { useCookies } from 'react-cookie';

const COOKIE_KEY = 'medipreco-cookie-consent';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

const defaultConsent = {
  necessary: true,
  advertising: false,
  performance: false,
  functionality: false,
  thirdParty: false,
};

export const CookieConsentContext = createContext({
  consent: defaultConsent,
  showBanner: false,
  showModal: false,
  openSettings: () => {},
  closeSettings: () => {},
  acceptAll: () => {},
  rejectAll: () => {},
  savePreferences: () => {},
});

export const useCookieConsent = () => useContext(CookieConsentContext);

export const CookieConsentProvider = ({ children }) => {
  const [cookies, setCookie] = useCookies([COOKIE_KEY]);

  const storedConsent = cookies[COOKIE_KEY];

  const [showBanner, setShowBanner] = useState(!storedConsent);
  const [showModal, setShowModal] = useState(false);
  const [consent, setConsent] = useState(() => storedConsent || defaultConsent);

  const saveConsent = useCallback((preferences) => {
    const consentData = {
      version: 1,
      timestamp: new Date().toISOString(),
      necessary: true,
      ...preferences,
    };

    setCookie(COOKIE_KEY, consentData, { path: '/', maxAge: COOKIE_MAX_AGE });
    setConsent(consentData);

    try {
      const existing = JSON.parse(localStorage.getItem('medipreco-consent-log') || '[]');
      existing.push({
        timestamp: consentData.timestamp,
        necessary: true,
        advertising: consentData.advertising,
        performance: consentData.performance,
        functionality: consentData.functionality,
        thirdParty: consentData.thirdParty,
      });
      localStorage.setItem('medipreco-consent-log', JSON.stringify(existing));
    } catch (_) {}

    setShowBanner(false);
    setShowModal(false);
  }, [setCookie]);

  const openSettings = useCallback(() => setShowModal(true), []);
  const closeSettings = useCallback(() => setShowModal(false), []);

  const acceptAll = useCallback(() => {
    saveConsent({ advertising: true, performance: true, functionality: true, thirdParty: true });
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    saveConsent({ advertising: false, performance: false, functionality: false, thirdParty: false });
  }, [saveConsent]);

  return (
    <CookieConsentContext.Provider value={{
      consent,
      showBanner,
      showModal,
      openSettings,
      closeSettings,
      acceptAll,
      rejectAll,
      savePreferences: saveConsent,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
};
